/*
 * Copyright (c) 2021 CalAmp Corp. All Rights Reserved
*/

import {translate} from '@ion/core/src/locale/locale';
import {getDateTimeForSearchType} from '@ion/core/src/services/ConnectUtils';
import {DeviceActivitySearchTypes, DeviceCommunicationSearchTypes, DeviceSearchTypes} from '@ion/core/src/types/services';
import {DeviceCommunicationResponseType, DeviceCommunicationTypeRequest} from './state';

/**
 * Returns the combined total of all the device communication counts.
 */
export const getTotal = (responses: DeviceCommunicationResponseType[]): number => {
  let total = 0;
  responses.forEach(response => {
    total += response.data?.response?.totalCount;
  });
  return total;
};

/**
 * Validates the responses of the combined device communication response.
 */
export const isValidResponse = (responses: DeviceCommunicationResponseType[]): boolean => {
  let isValid = true;
  responses.forEach(response => {
    if (!response || response.status !== 200 || !response.data) {
      isValid = false;
    }
  });
  return isValid;
};

export const MAX_LENGTH = translate('dashboard:widgets.deviceCommunication.checkDisplayLength');

/**
 * Validates the length of display count in dashboard device cards.
 */
export const validateDashboardCountDisplayLength = (
  deviceCommunicationReport: DeviceCommunicationTypeRequest): boolean => {
  if (deviceCommunicationReport) {
    const {lastSevenDays, lastFourteenDays, lastThirtyDays, lastNinetyDays} = deviceCommunicationReport;
    return (lastSevenDays?.toString().length > MAX_LENGTH ||
    lastFourteenDays?.toString().length > MAX_LENGTH ||
    lastThirtyDays?.toString().length > MAX_LENGTH ||
    lastNinetyDays?.toString().length > MAX_LENGTH);
  }

  return false;
};

/**
 * Determines the search query string based on the given search type.
 */
export const getQueryStringFromSearchType = (
  searchType: DeviceSearchTypes | DeviceCommunicationSearchTypes | DeviceActivitySearchTypes,
  searchTerm: string = null
): string => {
  switch (searchType) {
    case DeviceActivitySearchTypes.INACTIVE:
      return 'status:Disabled OR status:Suspended';
    case DeviceActivitySearchTypes.ACTIVE:
      return 'status: Enabled';
    case DeviceSearchTypes.CONFIGURATION_VERSION:
      return `extensions.configVersion: ${searchTerm}`;
    case DeviceSearchTypes.FIRMWARE_VERSION:
      return `extensions.firmwareVersion: ${searchTerm}`;
    case DeviceSearchTypes.DEVICE_MODEL:
      return `typeName: ${searchTerm}`;
    case DeviceCommunicationSearchTypes.LAST_7_DAYS:
    case DeviceCommunicationSearchTypes.LAST_14_DAYS:
    case DeviceCommunicationSearchTypes.LAST_30_DAYS:
    case DeviceCommunicationSearchTypes.LAST_90_DAYS:
      return `extensions.idReportTimestamp: <${getDateTimeForSearchType(searchType)}`;
    default:
      return '*id';
  }
};
